import {
  Component,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { FileMetadata, FileSrc } from 'src/app/models/helpers.model';
import { AttachmentsService } from 'src/app/services/attachments.service';
import {
  DocumentPrintPreferenceService,
  PRINT_LAYOUT_STAGE_DEFAULTS,
  PrintLayoutStage,
} from 'src/app/services/document-print-preference.service';
import { forkJoin, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-pdf-img-preview',
  templateUrl: './pdf-img-preview.component.html',
  styleUrls: ['./pdf-img-preview.component.scss'],
})
export class PdfImgPreviewComponent implements OnChanges, OnDestroy {
  @Input() fileData!: File | FileSrc;
  @Input() fileImgData!: File | FileSrc;
  /** ECDEV-174: document TEMPLATE id — the key the print-layout preference is remembered under. */
  @Input() documentId!: string;
  /**
   * ECDEV-365: whose first-visit default the switch opens with when this user has never set one for
   * this template. Stage 4 — the official documents Cold Storage prints onto pre-printed stock — is
   * the only host today, and is the default here for that reason; a Stage-3-side host must say so.
   */
  @Input() printStage: PrintLayoutStage = PrintLayoutStage.Stage4;
  @Output() modalState = new EventEmitter<{
    isClose: boolean;
  }>();
  /**
   * ECDEV-253: fires when the user sends this document to the printer. The browser's print
   * dialog is out of reach — we cannot tell whether the user then hit Print or Cancel — so this
   * reports the intent, which is what the Stage-4 indicator is meant to show.
   */
  @Output() printRequested = new EventEmitter<void>();
  /**
   * ECDEV-253: fires when the user downloads this document. Once the file has left, whether it
   * reaches a printer is out of our sight — so this is reported as a weaker signal than a print,
   * and consumers only surface it when the print button was never used.
   */
  @Output() downloadRequested = new EventEmitter<void>();
  showDoc = false;
  fileName!: string;
  fileImgName!: string;
  fileId!: string;
  fileImgId!: string;
  srcData!: Uint8Array | FileSrc;
  srcImgData!: Uint8Array | FileSrc;
  // ECDEV-174/365: opens on the default for `printStage` — OFF in Stage 4, where the form is
  // already on the paper. Overwritten from the remembered preference in ngOnChanges before the
  // viewer binds a src.
  showlayout = PRINT_LAYOUT_STAGE_DEFAULTS[this.printStage];
  allowSwitch = true;
  private destroy = new Subject<boolean>();
  constructor(
    private attachmentsService: AttachmentsService,
    private printPrefs: DocumentPrintPreferenceService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // ECDEV-174: apply the remembered choice before anything renders, so the viewer never shows
    // the wrong variant first. ECDEV-365: with nothing remembered this is the stage's default, not
    // a global OFF. Re-read on a printStage change too, so a host that resolves its stage late
    // still opens correctly.
    if (changes['documentId'] || changes['printStage']) {
      this.showlayout = this.printPrefs.get(this.documentId, this.printStage);
    }

    // Each input can change independently, so neither may be dereferenced unconditionally —
    // touching only one of them used to throw here.
    const changedValue = changes['fileData']?.currentValue;
    const changedImgValue = changes['fileImgData']?.currentValue;

    if (changedValue) {
      if (this.isFileSrc(changedValue)) {
        this.srcData = changedValue;
        this.fileId = changedValue.url.split('/').reverse()[0];
        this.getFileMeta(this.fileId);
        this.renderDoc();
      } else {
        this.fileName = changedValue.name;
        changedValue.arrayBuffer().then((b: any) => {
          this.srcData = new Uint8Array(b);
          this.renderDoc();
        });
      }
    }

    if (changedImgValue) {
      if (this.isFileSrc(changedImgValue)) {
        this.srcImgData = changedImgValue;
        this.fileImgId = changedImgValue.url.split('/').reverse()[0];
        this.getFileMeta(this.fileImgId);
        this.renderDoc();
      } else {
        this.fileImgName = changedImgValue.name;
        // was changedValue.arrayBuffer() — read the image-variant file, not the layout one.
        changedImgValue.arrayBuffer().then((b: any) => {
          this.srcImgData = new Uint8Array(b);
          this.renderDoc();
        });
      }
    }
  }

  // ECDEV-174: switching swaps to the other pre-rendered variant and remembers the choice for this
  // document template. The progress bar runs until the new render reports in via docLoaded().
  onPrintLayoutChange(showlayout: boolean): void {
    this.showlayout = showlayout;
    this.allowSwitch = false;
    this.printPrefs.set(this.documentId, showlayout);
  }

  isFileSrc(data: FileSrc | Uint8Array): data is FileSrc {
    return (data as FileSrc).withCredentials !== undefined;
  }

  docLoaded() {
    this.allowSwitch = true;
  }

  getFileMeta(id: string) {
    this.attachmentsService
      .getMetadata(id)
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (resp: FileMetadata) => {
          this.fileName = resp.fileName;
        },
      });
  }

  renderDoc() {
    if (this.srcData) {
      setTimeout(() => {
        (document.querySelector('.doc-content') as HTMLElement).style.width =
          '620px';
        (
          document.querySelector('.ng2-pdf-viewer-container') as HTMLElement
        ).style.width = '620px';
        this.showDoc = true;
      }, 500);
    } else {
      this.showDoc = false;
    }
  }

  backdropClick(event: Event) {
    if (event.target === event.currentTarget) {
      this.modalState.emit({ isClose: true });
    }
  }

  printDoc() {
    // ECDEV-253: announced up front rather than from the iframe's onload, so a slow or blocked
    // render still records that the user asked for this document on paper.
    this.printRequested.emit();

    let printData: File | FileSrc;
    let dataId: string;
    printData = this.fileData;
    dataId = this.fileId;
    if (!this.showlayout) {
      printData = this.fileImgData;
      dataId = this.fileImgId;
    }
    if (!this.isFileSrc(this.srcData)) {
      (printData as File).arrayBuffer().then((b) => {
        let arrBuffer = new Uint8Array(b);
        let dataView = new DataView(arrBuffer.buffer);
        const blob = new Blob([dataView], { type: 'application/pdf' });
        const frameEl = document.createElement('iframe');
        frameEl.style.display = 'none';
        frameEl.src = URL.createObjectURL(blob);
        document.body.appendChild(frameEl);
        frameEl.onload = () => {
          frameEl.contentWindow?.focus();
          frameEl.contentWindow?.print();
        };
      });
    } else {
      this.attachmentsService
        .getAttachment(dataId)
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: (resp: any) => {
            const blob = new Blob([resp], { type: 'application/pdf' });
            const frameEl = document.createElement('iframe');
            frameEl.style.display = 'none';
            frameEl.src = URL.createObjectURL(blob);
            document.body.appendChild(frameEl);
            frameEl.onload = () => {
              frameEl.contentWindow?.focus();
              frameEl.contentWindow?.print();
            };
          },
        });
    }
  }

  downloadDoc() {
    // ECDEV-253: announced up front, like printDoc, so a slow fetch still records the intent.
    this.downloadRequested.emit();

    let downloadData: File | FileSrc;
    let dataId: string;
    downloadData = this.fileData;
    dataId = this.fileId;
    if (!this.showlayout) {
      downloadData = this.fileImgData;
      dataId = this.fileImgId;
    }
    if (!this.isFileSrc(this.srcData)) {
      (downloadData as File).arrayBuffer().then((b) => {
        let arrBuffer = new Uint8Array(b);
        let dataView = new DataView(arrBuffer.buffer);
        const blob = new Blob([dataView], { type: 'application/pdf' });
        let anchorEl = document.createElement('a');
        anchorEl.href = URL.createObjectURL(blob);
        anchorEl.setAttribute('download', `${(downloadData as File).name}`);
        anchorEl.click();
      });
    } else {
      forkJoin([
        this.attachmentsService.getAttachment(dataId),
        this.attachmentsService.getMetadata(dataId),
      ])
        .pipe(takeUntil(this.destroy))
        .subscribe({
          next: (resp: [ArrayBuffer, FileMetadata]) => {
            const blob = new Blob([resp[0]], { type: 'application/pdf' });
            let anchorEl = document.createElement('a');
            anchorEl.href = URL.createObjectURL(blob);
            anchorEl.setAttribute('download', `${resp[1].fileName}`);
            anchorEl.click();
          },
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }
}
